import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AuthLayout from '../../components/layout/AuthLayout';
import Button from '../../components/common/Button';
import { useEmailMessageThreadApi } from '../../hooks/useEmailMessageThreadApi';
import { Star } from 'lucide-react';

const RatingThankYou: React.FC = () => {
  const { threadId, rating } = useParams<{ threadId: string; rating: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [ratingSubmitted, setRatingSubmitted] = useState<boolean>(false);
  
  const { setRating } = useEmailMessageThreadApi();

  useEffect(() => {
    const submitRating = async () => {
      if (!threadId || !rating) {
        setErrorMessage('Invalid rating link. Please check the URL.');
        setIsLoading(false);
        return;
      }

      const ratingNum = parseInt(rating, 10);
      if (isNaN(ratingNum) || ratingNum < 1 || ratingNum > 5) {
        setErrorMessage('Invalid rating value. Rating must be between 1 and 5.');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        await setRating(threadId, ratingNum);
        setRatingSubmitted(true);
      } catch (error: any) {
        console.error('Error submitting rating:', error);
        
        if (error?.response?.data?.message) {
          setErrorMessage(error.response.data.message);
        } else if (error?.message?.includes('already')) {
          setErrorMessage('You have already submitted a rating for this support request. Thank you!');
        } else {
          setErrorMessage('An error occurred while processing your rating. Please try again later.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    submitRating();
  }, [threadId, rating]);

  const getRatingText = (ratingValue: number): string => {
    switch (ratingValue) {
      case 5: return 'Excellent';
      case 4: return 'Good';
      case 3: return 'Average';
      case 2: return 'Below Average';
      case 1: return 'Poor';
      default: return ratingValue.toString();
    }
  };

  const getStars = (ratingValue: number): JSX.Element[] => {
    return Array.from({ length: ratingValue }, (_, index) => <Star key={index} size={35}/>);
  };

  // Loading state
  if (isLoading) {
    return (
      <AuthLayout
        title="Submitting Your Rating"
        description="Please wait while we process your feedback..."
      >
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      </AuthLayout>
    );
  }

  if (errorMessage) {
    return (
      <AuthLayout
        title="Unable to Submit Rating"
        description={errorMessage}
      >
        <div className="mt-8">
          <p className="text-sm text-gray-400 text-center mb-6">
            {errorMessage.includes('already submitted') 
              ? 'We appreciate you taking the time to rate your support experience.'
              : 'If the problem persists, please contact our support team.'}
          </p>
        </div>
      </AuthLayout>
    );
  }

  const ratingNum = parseInt(rating || '0', 10);
  const ratingText = getRatingText(ratingNum);
  const stars = getStars(ratingNum);

  return (
    <AuthLayout
      title="Thank You!"
      description="We appreciate you taking the time to rate your support experience."
    >
      <div className="mt-8 text-center">

        {/* Rating display */}
        <div className="mb-4">
          <div className="flex justify-center text-4xl letter mb-3" style={{ letterSpacing: '0.25rem' }}>
            {stars}
          </div>
          <div className="text-2xl font-semibold text-primary mb-4">
            {ratingText}
          </div>
        </div>

        {/* Message */}
        <p className="text-base text-gray-400 mb-8 leading-relaxed">
          Your feedback helps us improve our service and better assist our members.
        </p>

        {/* Footer */}
        <div className="pt-6 border-t border-gray-700">
          <p className="text-sm text-gray-500 mb-4">
            You can now close this window.
          </p>
          <p className="text-sm text-gray-400">
            The Natshoot Team
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};

export default RatingThankYou;
